// ChatLinkGenerator.js
import React, { useState } from 'react';
import {
  Box,
  Typography,
  Button,
  TextField,
  IconButton,
  Tooltip,
  CircularProgress,
  Snackbar,
  Alert,
  Paper,
  Divider
} from '@mui/material';
import {
  ContentCopy,
  Share,
  CheckCircle
} from '@mui/icons-material';
import { createChatInvite, copyInviteUrl, shareInviteLink } from './MessengerInviteUtils';

/**
 * Component for generating and managing chat invitation links
 */
const ChatLinkGenerator = ({ onClose }) => {
  const [inviteUrl, setInviteUrl] = useState('');
  const [generating, setGenerating] = useState(false);
  const [copied, setCopied] = useState(false);
  const [error, setError] = useState(null);
  const [successMessage, setSuccessMessage] = useState('');
  
  // Get therapist information from localStorage
  const therapistUuid = localStorage.getItem('userUuid') || '';
  const therapistName = localStorage.getItem('userName') || 'Your Therapist';
  
  // Generate a new invite link
  const handleGenerateInvite = async () => {
    setGenerating(true);
    setError(null);
    
    try {
      const result = await createChatInvite(therapistUuid, therapistName);
      setInviteUrl(result.inviteUrl);
    } catch (err) {
      console.error('Failed to generate invite:', err);
      setError('Failed to generate invite link. Please try again.');
    } finally {
      setGenerating(false);
    }
  };
  
  // Copy the invite URL to clipboard
  const handleCopyInvite = async () => {
    if (!inviteUrl) return;
    
    const success = await copyInviteUrl(inviteUrl);
    if (success) {
      setCopied(true);
      setSuccessMessage('Invite link copied to clipboard!');
      setTimeout(() => setCopied(false), 3000);
    } else {
      setError('Failed to copy invite link. Please try again.');
    }
  };
  
  // Share the invite using the Web Share API if available
  const handleShareInvite = async () => {
    if (!inviteUrl) return;
    
    const success = await shareInviteLink(inviteUrl, therapistName);
    if (success) {
      setSuccessMessage('Invite link shared successfully!');
    } else {
      // Likely already handled by the copyInviteUrl fallback
    }
  };
  
  // Clear success message
  const handleSuccessClose = () => {
    setSuccessMessage('');
  };
  
  return (
    <>
      <Paper elevation={3} sx={{ p: 3, mb: 3 }}>
        <Typography variant="body2" color="text.secondary" sx={{ mb: 3 }}>
          Generate a link to invite clients to chat with you. The link will be valid for 7 days.
        </Typography>
        
        {inviteUrl ? (
          <Box sx={{ mb: 2 }}>
            <Box sx={{ 
              display: 'flex',
              alignItems: 'center',
              bgcolor: 'action.hover',
              borderRadius: 1,
              p: 1
            }}>
              <TextField
                fullWidth
                variant="outlined"
                size="small"
                value={inviteUrl}
                InputProps={{
                  readOnly: true,
                }}
              />
              <Tooltip title={copied ? "Copied!" : "Copy to clipboard"}>
                <IconButton 
                  onClick={handleCopyInvite}
                  color={copied ? "success" : "primary"}
                  sx={{ ml: 1 }}
                >
                  {copied ? <CheckCircle /> : <ContentCopy />}
                </IconButton>
              </Tooltip>
              <Tooltip title="Share invite">
                <IconButton 
                  onClick={handleShareInvite}
                  color="primary"
                  sx={{ ml: 1 }}
                >
                  <Share />
                </IconButton>
              </Tooltip>
            </Box>
            <Typography variant="caption" color="text.secondary" sx={{ mt: 1, display: 'block' }}>
              Share this link with your client to start a conversation.
            </Typography>
            
            <Box sx={{ mt: 2, display: 'flex', justifyContent: 'space-between' }}>
              {onClose && (
                <Button onClick={onClose} variant="outlined" size="small">
                  Close
                </Button>
              )}
              <Button 
                variant="contained" 
                color="primary"
                onClick={handleGenerateInvite}
                disabled={generating}
                size="small"
                sx={{ borderRadius: 20 }}
              >
                Generate New Link
              </Button>
            </Box>
          </Box>
        ) : (
          <Box sx={{ textAlign: 'center', py: 2 }}>
            {error && (
              <Typography color="error" variant="body2" sx={{ mb: 2 }}>
                {error}
              </Typography>
            )}
            <Button
              variant="contained"
              color="primary"
              onClick={handleGenerateInvite}
              disabled={generating}
              startIcon={generating ? <CircularProgress size={20} /> : null}
              sx={{ borderRadius: 20 }}
              size="small"
            >
              {generating ? 'Generating...' : 'Generate Invite Link'}
            </Button>
            
            {onClose && (
              <Button 
                onClick={onClose} 
                variant="text" 
                sx={{ mt: 1 }}
                size="small"
              >
                Cancel
              </Button>
            )}
          </Box>
        )}
      </Paper>
      
      {/* Success message snackbar */}
      <Snackbar 
        open={!!successMessage} 
        autoHideDuration={4000} 
        onClose={handleSuccessClose}
        anchorOrigin={{ vertical: 'bottom', horizontal: 'center' }}
      >
        <Alert onClose={handleSuccessClose} severity="success" sx={{ width: '100%' }}>
          {successMessage}
        </Alert>
      </Snackbar>
    </>
  );
};

export default ChatLinkGenerator;